<template>
  <div
    class="editor-canvas-carousel"
    :class="{ 'editor-canvas-carousel--animated': animationsEnabled }"
    @scroll="onScroll"
    v-on="editorIsFullscreen
      ? {
        touchstart: onTouchStart,
        touchmove: onTouchMove,
        touchend: onTouchEnd,
      }
      : {}"
  >
    <div
      ref="canvasWrappers"
      :style="{ ...canvasWrappersStyle, ...canvasWrappersOverrideStyle }"
      class="editor-canvas-carousel__canvas-wrappers"
    >
      <div
        v-for="page in pages"
        :key="page.id"
        :style="canvasWrapperStyle"
        :class="[
          { 'editor-canvas-carousel__canvas-wrapper--hidden': !canvasWrapperVisible(page) },
          editorThemeClasses(page),
        ]"
        class="editor-canvas-theme editor-canvas-carousel__canvas-wrapper"
      >
        <template v-if="renderPage(page.id)">
          <component v-if="page.slotPage" :is="page.slotPage?.vnode" />
          <template v-else>
            <CanvasBackgroundImage
              :key="`canvas-background-image-${page.id}`"
              :page-id="page.id"
              :is-fullscreen="editorIsFullscreen"
            />
            <FullSlideAsset
              v-if="isAssetSlide(page)"
              :key="`full-slide-asset-${page.id}`"
              :page="page"
              :scale="scale"
              :is-fullscreen="editorIsFullscreen"
              :mode="mode"
              :is-displayed="pageIsDisplayed(page)"
            />
            <EditorCanvas
              ref="editorCanvases"
              class="grid-canvas"
              :class="{ 'grid-canvas--hidden': mode === 'VIEW' }"
              :mode="mode"
              :key="`editor-canvas-${page.id}-${mode}`"
              :scroll-container="scrollContainer"
              :scale="scale"
              :page-id="page.id"
              :menu-container="menuContainer"
              :snap-grid="snapGrid"
              :module-animations-enabled="moduleAnimationsEnabled"
              :force-displayed="!!pageId"
              @height-updated="$emit('height-updated', $event)"
            />
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { first, get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import EditorCanvas from '@/components/EditorCanvas.vue';
import FullSlideAsset from '@/components/full-slide-asset/FullSlideAsset.vue';
import CanvasBackgroundImage from '@/components/CanvasBackgroundImage.vue';
import { canUseWebAnimationsApi } from '@/lib/utils';
import { bus } from '@/lib/eventBus';
import * as types from '@/lib/constants/store';

import {
  EDITOR_CANVAS_WIDTH,
} from '@/lib/constants';
import {
  useEditorStore,
  useModalStore,
  useUserStore,
} from '@/stores';

/*
  Custom animations that can be used for page navigation.
*/
const carouselAnimations = {
  slideIn(args) {
    const keyframes = [
      { left: `${args.currentLeft}px` },
      { left: `${args.targetLeft}px` },
    ];
    const config = {
      easing: 'ease-out',
      duration: 750,
      iterations: 1,
    };
    return { keyframes, config };
  },
  slideInWithBounce(args) {
    const bounceDistance = 20;
    const firstBounce = bounceDistance * args.direction;
    const firstBounceBack = (-1 * args.direction) * (bounceDistance * 0.5);
    const keyframes = [
      { left: `${args.currentLeft}px` },
      {
        left: `${args.targetLeft + firstBounce}px`,
        offset: 0.6,
      },
      {
        left: `${args.targetLeft + firstBounceBack}px`,
        offset: 0.75,
      },
      { left: `${args.targetLeft}px` },
    ];
    const config = {
      easing: 'ease-out',
      duration: 1400,
      iterations: 1,
    };
    return { keyframes, config };
  },
};

export default {
  components: {
    EditorCanvas,
    FullSlideAsset,
    CanvasBackgroundImage,
  },
  emits: [
    'height-updated',
  ],
  props: {
    mode: {
      type: String,
      default: 'VIEW',
    },
    scale: {
      type: Number,
      default: 1,
    },
    scrollContainer: {
      type: HTMLElement,
      default: null,
    },
    pageId: {
      type: String,
      default: null,
    },
    menuContainer: {
      type: HTMLElement,
      default: null,
    },
    snapGrid: {
      type: Object,
      default: undefined,
    },
    moduleAnimationsEnabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      carouselPageIndex: null,
      canvasWrappersOverrideStyle: null,
      wrappersAnimation: null,
      canvasWrapperStyle: {
        width: `${EDITOR_CANVAS_WIDTH}px`,
      },
      animatingPageCache: null,
    };
  },
  mounted() {
    if (this.mode === 'VIEW') {
      this.attachHandlers();
    }
    this.updateCarouselPageIndex();
  },
  beforeUnmount() {
    this.removeHandlers();
  },
  watch: {
    currentPageIndex(toIndex, fromIndex) {
      /*
        Navigate to the current page when it changes. If animations are
        enabled, then animate the transition, otherwise go directly to the page.
      */
      if (this.animationsEnabled) {
        bus.emit('editor-canvas-carousel:start-page-change-animation');
        /*
          Add the currently rendered pages to the page cache so they stay
          rendered while animating.
        */
        this.cachePages(fromIndex);
        /*
          Use a predefined animation to transition to the new page.
        */
        this.animateToCurrentPageIndex(carouselAnimations.slideIn);
      } else {
        this.updateCarouselPageIndex();
      }
    },
    mode() {
      if (this.mode === 'VIEW') {
        this.attachHandlers();
      } else {
        this.removeHandlers();
        if (this.wrappersAnimation) {
          this.cancelAnimation();
          this.updateCarouselPageIndex();
        }
      }
    },
    animating() {
      if (!this.animating) {
        // Clear the rendered page cache when animating finishes.
        this.animatingPageCache = null;
      }
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentPageIndex',
      'currentPage',
      'getOrderedModulesByModalId',
      'getOrderedModulesByPageId',
      'pagesSorted',
      'draftType',
      'displayedPageId',
      'draft',
      'editorIsFullscreen',
      'currentModalCanvasId',
      'editorCarouselIsAnimated',
    ]),
    ...mapState(useModalStore, [
      'modalIsOpen',
    ]),
    ...mapState(useUserStore, [
      'isRtl',
    ]),
    pages() {
      if (this.pageId) {
        return this.pagesSorted.filter((sortedPage) => sortedPage.id === this.pageId);
      }
      if (this.animationsEnabled) {
        return this.pagesSorted;
      }
      return this.pagesSorted.slice(this.currentPageIndex, this.currentPageIndex + 1);
    },
    animationsEnabled() {
      return this.editorCarouselIsAnimated && this.useWebAnimationsApi
        && this.draftType !== 'board' && this.mode === 'VIEW' && !this.pageId;
    },
    canvasWrappersStyle() {
      const placementKey = this.isRtl ? 'right' : 'left';

      return {
        [placementKey]: `${this.getCanvasWrappersLeft(this.carouselPageIndex)}px`,
        width: `${(this.animationsEnabled ? this.pagesSorted.length : 1) * EDITOR_CANVAS_WIDTH}px`,
      };
    },
    navigationKeysEnabled() {
      return this.mode === 'VIEW' && !this.pageId
        && !this.currentModalCanvasId && !this.modalIsOpen;
    },
    animating() {
      return this.wrappersAnimation || this.canvasWrappersOverrideStyle;
    },
    useWebAnimationsApi() {
      return canUseWebAnimationsApi();
    },
    renderedPageIds() {
      const currentPageIds = this.getRenderedPageIds();
      const pageCache = this.animatingPageCache || new Set();
      const renderedPageIds = new Set([...currentPageIds, ...pageCache]);
      return renderedPageIds;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.VIEW_PREV_PAGE,
      types.VIEW_NEXT_PAGE,
      types.SET_EDITOR_CAROUSEL_PAGE_INDEX,
    ]),
    getCanvasRef() {
      return first(this.$refs.editorCanvases);
    },
    attachHandlers() {
      bus.on('document:keyup', this.onKeyup);
    },
    removeHandlers() {
      bus.off('document:keyup', this.onKeyup);
    },
    editorThemeClasses(page) {
      const themeName = get(page, 'options.theme.name', 'default');
      const themeVariation = get(page, 'options.theme.variation', 1);

      return [
        themeName && `editor-theme--${themeName}`, // used for button and text themes
        themeName && `editor-theme--${themeName}-${themeVariation}`, // used for background colors
      ];
    },
    pageIsDisplayed(page) {
      return page.id === this.pageId || this.displayedPageId === page.id;
    },
    cachePages(pageIndex) {
      const pageCache = new Set(this.animatingPageCache ? [...this.animatingPageCache] : []);
      const pageIds = this.getRenderedPageIds(pageIndex);
      pageIds.forEach((id) => {
        pageCache.add(id);
      });
      this.animatingPageCache = pageCache;
    },
    getRenderedPageIds(forPageIndex) {
      const pageIndex = forPageIndex !== undefined ? forPageIndex : this.currentPageIndex;
      const startIndex = pageIndex > 0 ? pageIndex - 1 : 0;
      const endIndex = pageIndex + 2;
      return this.pagesSorted.slice(startIndex, endIndex).map((page) => page.id);
    },
    renderPage(pageId) {
      if (this.pageId) {
        return pageId === this.pageId;
      }
      if (!this.animationsEnabled) {
        return this.currentPage?.id === pageId;
      }
      return this.renderedPageIds.has(pageId);
    },
    canvasWrapperVisible(page) {
      if (!this.animationsEnabled) return true;
      const pageIndex = this.pagesSorted.findIndex((sortedPage) => sortedPage.id === page.id);
      return pageIndex === this.carouselPageIndex || this.animating;
    },
    updateCarouselPageIndex() {
      this.carouselPageIndex = this.pageId
        ? this.pagesSorted.findIndex((page) => page.id === this.pageId)
        : this.currentPageIndex;
      this.animatingPageCache = null;
      this[types.SET_EDITOR_CAROUSEL_PAGE_INDEX](this.carouselPageIndex);
    },
    cancelAnimation() {
      this.wrappersAnimation.cancel();
      this.wrappersAnimation = null;
    },
    animateToCurrentPageIndex(animation) {
      const wrappersEl = this.$refs?.canvasWrappers;
      if (!wrappersEl) {
        this.updateCarouselPageIndex();
        return;
      }

      const currentLeft = parseInt(getComputedStyle(wrappersEl).left, 10);
      const targetLeft = this.getCanvasWrappersLeft(this.currentPageIndex);
      const direction = targetLeft < currentLeft ? -1 : 1;
      const maxAnimationDistance = EDITOR_CANVAS_WIDTH * 3 + 1; // Three slides
      const animationDistance = Math.abs(currentLeft - targetLeft);

      // If we're already animating, cancel it.
      if (this.wrappersAnimation) {
        this.cancelAnimation();
      }

      /*
        Don't use animation if the slide is too far away since it doesn't
        look very nice in the UI. Instead go directly to the page without animating.
      */
      if (animationDistance > maxAnimationDistance) {
        this.updateCarouselPageIndex();
        return;
      }

      /*
        Call the provided function that returns the animation to use for this page navigation.
      */
      const { keyframes, config } = animation({ currentLeft, targetLeft, direction });
      this.wrappersAnimation = wrappersEl.animate(keyframes, config);
      this.wrappersAnimation.onfinish = this.onWrappersAnimationFinish;
    },
    onWrappersAnimationFinish() {
      this.updateCarouselPageIndex();
      this.wrappersAnimation = null;
      this.canvasWrappersOverrideStyle = null;
    },
    getCanvasWrappersLeft(pageIndex) {
      return this.animationsEnabled
        ? pageIndex * EDITOR_CANVAS_WIDTH * -1
        : 0;
    },
    isAssetSlide(page) {
      return !!get(page, 'options.asset_id');
    },
    onKeyup({ key, target }) {
      // If nav keys disabled, don't allow nav to another page.
      if (!this.navigationKeysEnabled) return;

      // Don't navigate if we're editing text, selecting a radio button, using player controls,
      // or moving around a TEI tiling term
      if (target.closest('.multiple-choice-viewer, .text-editor, input, textarea, .tei-tiling-term, .embedded-player__container')) return;

      let action = null;
      if (['Left', 'ArrowLeft'].includes(key)) {
        action = this.isRtl ? types.VIEW_NEXT_PAGE : types.VIEW_PREV_PAGE;
      } else if (['Right', 'ArrowRight'].includes(key)) {
        action = this.isRtl ? types.VIEW_PREV_PAGE : types.VIEW_NEXT_PAGE;
      }
      if (action) {
        this[action]();
      }
    },
    clearTouchData() {
      this.clearTouchStartData();
      this.canvasWrappersOverrideStyle = null;
    },
    clearTouchStartData() {
      this.touchStartX = undefined;
      this.touchStartTime = undefined;
    },
    onTouchStart(event) {
      /*
        If we're already animating or interacting with an interactive
        element, then clear touch data and exit.
      */
      const isInteractiveElement = event.target.closest('.vjs-control-bar, .ProseMirror, input, textarea');
      if (this.wrappersAnimation || isInteractiveElement) {
        this.clearTouchData();
        return;
      }
      this.touchStartX = first(event.touches).screenX;
      this.touchStartTime = Date.now();
      this.touchMoveStartEventFired = false;
    },
    onTouchMove(event) {
      if (this.wrappersAnimation) {
        this.clearTouchData();
        return;
      }
      if (this.touchStartX === undefined) return;

      if (!this.touchMoveStartEventFired) {
        // Fire an event when touch moving first starts.
        bus.emit('editor-canvas-carousel:start-touch-move-animation');
        this.touchMoveStartEventFired = true;
      }

      const touchMoveX = first(event.touches).screenX;
      const touchMoveTime = Date.now();
      const swipeDistance = Math.abs(touchMoveX - this.touchStartX) / this.scale;
      const swipeTime = touchMoveTime - this.touchStartTime;
      const startLeft = this.getCanvasWrappersLeft(this.carouselPageIndex);
      const changePageDistance = EDITOR_CANVAS_WIDTH / 2;
      const canViewNextPage = this.carouselPageIndex < (this.pagesSorted.length - 1);
      const canViewPreviousPage = this.carouselPageIndex > 0;
      const canChangePage = touchMoveX < this.touchStartX ? canViewNextPage : canViewPreviousPage;

      /*
        If the swipe has moved enough distance in a short amount of time or has moved
        over half the distance of the slide, then go to the next page in the swipe direction.
      */
      const changePage = canChangePage
        && ((swipeDistance > 150 && swipeTime < 300) || swipeDistance > changePageDistance);

      // If there are no more pages in this direction, then exit.
      if (!canChangePage) return;

      // Swipe left
      if (touchMoveX < this.touchStartX) {
        if (this.animationsEnabled) {
          this.canvasWrappersOverrideStyle = {
            ...this.canvasWrappersStyle,
            left: `${startLeft - swipeDistance}px`,
          };
        }
        if (changePage) {
          this[types.VIEW_NEXT_PAGE]();
          this.clearTouchStartData();
        }
      }
      // Swipe right
      if (touchMoveX > this.touchStartX) {
        if (this.animationsEnabled) {
          this.canvasWrappersOverrideStyle = {
            ...this.canvasWrappersStyle,
            left: `${startLeft + swipeDistance}px`,
          };
        }
        if (changePage) {
          this[types.VIEW_PREV_PAGE]();
          this.clearTouchStartData();
        }
      }
    },
    onTouchEnd(event) {
      if (!event.touches?.length) {
        /*
          If we dragged the slide out but didn't complete the navigation,
          then animate back to the slide's original position.
        */
        if (this.canvasWrappersOverrideStyle) {
          this.animateToCurrentPageIndex(carouselAnimations.slideIn);
        }
        this.clearTouchData();
      }
    },
    onScroll(event) {
      // Shouldn't happen, but prevent any scrolling just in case.
      event.target.scrollTo(0, 0);
    },
  },
};
</script>

<style lang="stylus">
.editor-canvas-carousel {
  height: 100%;
  position: relative;
  width: 100%;

  &--animated {
    overflow: hidden;
  }

  &__canvas-wrappers {
    display: flex;
    height: 100%;
    position: absolute;
  }

  &__canvas-wrapper {
    position: relative;

    &--hidden * {
      // Prevent tabbing into hidden pages.
      visibility: hidden !important;
    }
  }

  .grid-canvas {
    transform: translate3d(0, 0, 0); // prevents leftover lines after animation
    &--hidden {
      overflow: hidden; // prevents image overlay the canvas that follows
    }
  }
}
</style>
